import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
} from '@mui/material';
import { RiCloseLine } from '@remixicon/react';

/**
 * Color Picker Modal - Opens beside RightSidebar for selecting colors
 * 
 * Used for Fill Color and Stroke Color selection
 */
const ColorPickerModal = ({ open, onClose, currentColor, onColorSelect, title = 'Select Color' }) => {
  const [selectedColor, setSelectedColor] = useState(currentColor || '#6B22D5');

  useEffect(() => {
    if (open && currentColor) {
      setSelectedColor(currentColor);
    }
  }, [open, currentColor]);

  const presetColors = [
    '#FFFFFF',
    '#F5F5F5',
    '#E0E0E0',
    '#000000',
    '#C61F2F',
    '#E42436',
    '#6B22D5',
    '#5B1DB7',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#78CE75',
  ];

  const handleColorSelect = (color, closeAfterSelect = false) => {
    setSelectedColor(color);
    if (onColorSelect) {
      onColorSelect(color);
    }
    if (closeAfterSelect) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 320, // Position beside RightSidebar (width: 320)
        top: 64, // Below TopHeaderBar (height: 64)
        width: 300,
        height: 'calc(100vh - 64px)',
        backgroundColor: 'white',
        borderLeft: '1px solid #E0E0E0',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.1)',
        zIndex: 999,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 200px)',
        borderRadius: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <RiCloseLine size={20} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Color Picker */}
        <Box sx={{ marginBottom: '50px' }}>
          <TextField
            type="color"
            value={selectedColor}
            onChange={(e) => {
              handleColorSelect(e.target.value);
            }}
            sx={{
              width: '100%',
              height: '60px',
              '& input': {
                height: '60px',
                cursor: 'pointer',
              },
            }}
          />
        </Box>

        {/* Hex Input */}
        <Box sx={{ marginBottom: '24px' }}>
          <TextField
            label="Hex Color"
            value={selectedColor.replace('#', '').toUpperCase()}
            onChange={(e) => {
              const value = e.target.value.replace('#', '');
              if (value.length === 6) {
                const color = `#${value}`;
                handleColorSelect(color);
              }
            }}
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                '& input': {
                  textTransform: 'uppercase',
                },
              },
            }}
          />
        </Box>

        {/* Preset Colors */}
        <Typography variant="subtitle2" sx={{ marginBottom: '12px' }}>
          Preset Colors
        </Typography>
        <Grid container spacing={1}>
          {presetColors.map((color) => (
            <Grid item key={color}>
              <Box
                onClick={() => handleColorSelect(color, true)}
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: color,
                  border:
                    selectedColor === color
                      ? '3px solid #6B22D5'
                      : '1px solid #E0E0E0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default ColorPickerModal;

